import { Button, Intent } from "@blueprintjs/core";
import { FGCheckboxInput, FGSelectInput, FGTextInput, FieldGroup, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { ArticleApi, ArticleDto, ArticleTranslationDto, EUserProfile } from "../../../../api";
import { FGMultiTranslate, SmallFormGenerator } from "../../../../components";
import { FGEuroMaskInput } from "../../../../components/formGenerator/FGEuroMaskInput";
import { useAuth, useDialog, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IArticleDetailPageProps {
  articleId: number;
  editMode: boolean;
}

export const ArticleDetailPage: React.FunctionComponent<IArticleDetailPageProps> = ({ articleId, editMode }) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(ArticleApi);
  const { languages } = useGlobalData();
  const { user } = useAuth();
  const [saveBcLoading, setsaveBcLoading] = React.useState(false);
  const [vatCodes, vLoading] = useReferential((c) => c.getVatCode(), false);
  const { showDialogPromise } = useDialog();

  const { data, loading, saveItem, saving, refresh } = useCrudApi(
    {
      getApiFn: async (id = articleId) => {
        if (id > 0) {
          var res = await api.getArticle(id);
          res["formatedPrice"] = res.price.toString().replace(".", ",");
          return res;
        }
        return new ArticleDto({
          isMiscelaneous: true,
          articleTranslations: [
            ...languages.map(
              (l) =>
                new ArticleTranslationDto({
                  languageId: l.languageId,
                })
            ),
          ],
        }).toJSON();
      },
      saveApiFn: (d: ArticleDto) => api.saveArticle(d),
      onSavedRoute: (d) => `${ERoutes.article}/${d.articleId}/detail/edit`,
      deleteApiFn: () => {},
      onDeletedRoute: (d) => `${ERoutes.article}`,
    },
    [articleId]
  );

  const saveBc = React.useCallback(async () => {
    setsaveBcLoading(true);
    const dialogResult = await showDialogPromise({
      message: t(ETLCodes.BCSaveConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setsaveBcLoading(false);
      return;
    }
    try {
      await api.saveBcArticle(data?.articleId);
      showSuccess(t(ETLCodes.SaveSuccess));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    }
    setsaveBcLoading(false);
  }, [api, data, refresh, showDialogPromise, t]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      vatCode: Yup.string().nullable().required(t(ETLCodes.Required)),
      // articleTranslations: Yup.array().of(
      //   Yup.object().shape({
      //     text: Yup.string().nullable().required(t(ETLCodes.Required)),
      //   })
      // ),
    });
  }, [t]);

  const save = React.useCallback(
    async (d: ArticleDto) => {
      d.price = d["formatedPrice"].replace(",", ".");
      return await saveItem(d);
    },
    [saveItem]
  );

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={save}
      editMode={editMode}
      loading={loading}
      onCancel={() => history.push(ERoutes.article)}
      saving={saving}
      validationSchema={FormSchema}
    >
      <FieldGroup
        fieldsetProps={{
          title: t(ETLCodes.Article),
          rightElement: (
            <>
              {data && !data.bcIdentifier ? (
                <Button onClick={saveBc} text={t(ETLCodes.SaveNav)} intent={Intent.PRIMARY} loading={saveBcLoading} />
              ) : null}
            </>
          ),
        }}
      >
        {user?.profile === EUserProfile.God && <FGTextInput label={t(ETLCodes.Code)} name="code" readonly />}
        <FGTextInput label={t(ETLCodes.Identifier)} name="articleNumber" readonly />
        {data?.bcIdentifier ? (
          <FGTextInput label={t(ETLCodes.BCIdentifier)} name="bcIdentifier" readonly />
        ) : (
          <FGCheckboxInput label={t(ETLCodes.BCIdentifier)} name="BcIdentifier" readonly />
        )}
        <FGEuroMaskInput name="formatedPrice" label={t(ETLCodes.EVAT_Price)} />
        <FGSelectInput
          readonly={!!data?.bcIdentifier}
          name="vatCode"
          label={t(ETLCodes.VatPercentage)}
          items={vatCodes}
          loading={vLoading}
        />
        <FGMultiTranslate
          maxlength={100}
          label={ETLCodes.Name}
          translationsArrayField="articleTranslations"
          translationField="text"
          translationDto={ArticleTranslationDto}
        />
      </FieldGroup>
    </SmallFormGenerator>
  );
};
